"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.I18N = void 0;
var config_1 = require("../types/config/config");
var tax_mode_1 = require("../types/config/tax-mode");
var date_format_options_1 = require("../types/functions/date-format-options");
var number_parse_options_1 = require("../types/functions/number-parse-options");
var format_date_1 = require("./functions/date/format-date");
var get_date_format_string_1 = require("./functions/date/utils/get-date-format-string");
var parse_date_1 = require("./functions/date/utils/parse-date");
var format_currency_1 = require("./functions/format-currency");
var format_number_1 = require("./functions/format-number");
var format_person_name_1 = require("./functions/format-person-name");
var format_template_1 = require("./functions/format-template");
var format_translatable_1 = require("./functions/format-translatable");
var merge_deep_right_1 = require("./functions/merge-deep-right");
var parse_number_1 = require("./functions/parse-number");
/**
 * A collection of internationalisation helper functions.
 */
var I18N = /** @class */ (function () {
    /**
     * Default constructor for I18N.
     * @param config The config options.
     */
    function I18N(config) {
        var _this = this;
        /**
         * Merges the provided `DateFormatOptions` with the configured values.
         *
         * @hidden
         * @param options The provided date format options.
         * @returns The merged date format options.
         */
        this.getDateFormatOptions = function (options) {
            return __assign({ languageTag: _this.config.languageTag, format: date_format_options_1.DateFormat.ISO }, merge_deep_right_1.mergeDeepRight(_this.config.date, options));
        };
        /**
         * Merges the provided `NumberFormatOptions` with the configured values.
         *
         * @hidden
         * @param options The provided number format options.
         * @returns The merged number format options.
         */
        this.getNumberFormatOptions = function (options) {
            return merge_deep_right_1.mergeDeepRight(_this.config.number, options);
        };
        /**
         * Merges the provided `CurrencyFormatOptions` with the configured values.
         *
         * @hidden
         * @param options The provided currency format options.
         * @returns The merged currency format options.
         */
        this.getCurrencyFormatOptions = function (options) {
            return merge_deep_right_1.mergeDeepRight(_this.config.currency, options);
        };
        /**
         * Merges the provided `CurrencyFormatOptions` with the configured values.
         *
         * @hidden
         * @param options The provided currency format options.
         * @returns The merged currency format options.
         */
        this.getMonetaryValueFormatOptions = function (options) {
            return merge_deep_right_1.mergeDeepRight(__assign(__assign({}, _this.config.currency), _this.config.monetaryValue), options);
        };
        /**
         * Merges the provided `NameFormatOptions` with the configured values.
         *
         * @hidden
         * @param options The provided name format options.
         * @returns The merged name format options.
         */
        this.getNameFormatOptions = function (options) {
            return merge_deep_right_1.mergeDeepRight(_this.config.personName, options);
        };
        /**
         * Merges the provided `TranslatableFormatOptions` with the configured values.
         *
         * @hidden
         * @param options The provided translatable format options.
         * @returns The merged translatable format options.
         */
        this.getTranslatableLanguageTag = function (options) {
            if (options === void 0) { options = {}; }
            if (options.languageTag)
                return options.languageTag;
            return _this.config.languageTag;
        };
        /**
         * Merges the provided `TemplateFormatOptions` with the configured values.
         *
         * @hidden
         * @param options The provided template format options.
         * @returns The merged template format options.
         */
        this.getTemplateFormatOptions = function (options) {
            if (options === void 0) { options = {}; }
            return {
                date: _this.getDateFormatOptions(options.date),
                number: _this.getNumberFormatOptions(options.number),
                currency: _this.getCurrencyFormatOptions(options.currency),
                personName: _this.getNameFormatOptions(options.personName)
            };
        };
        /**
         * Gets the decimal separator for number parsing based on the provided `TemplateFormatOptions` and configured values.
         *
         * @hidden
         * @param options The provided number parse options.
         * @returns The decimal separator to be used.
         */
        this.getDecimalSeparatorForParse = function (options) {
            if (options === void 0) { options = {}; }
            var type = options.type, decimalSeparator = options.decimalSeparator;
            if (decimalSeparator)
                return decimalSeparator;
            switch (type) {
                case number_parse_options_1.NumberType.Currency:
                    return _this.config.currency.decimalSeparator;
                case number_parse_options_1.NumberType.Number:
                default:
                    return _this.config.number.decimalSeparator;
            }
        };
        this.setConfig(config);
    }
    /**
     * Configuration getter
     */
    I18N.prototype.getConfig = function () {
        return this.config;
    };
    /**
     * Sets the config to be used for formatting.
     * Merges the partial config provided with the default.
     *
     * @param config The config options.
     */
    I18N.prototype.setConfig = function (config) {
        this.config = merge_deep_right_1.mergeDeepRight(config_1.defaultConfig, config);
    };
    /**
     * Formats a given `DateInput`, based on the merged `DateFormatOptions`.
     *
     * @param value The `DateInput` value to be formatted.
     * @param options The date format option overrides to be used (optional).
     * @returns The formatted date string.
     */
    I18N.prototype.date = function (value, options) {
        return format_date_1.formatDate(value, this.getDateFormatOptions(options));
    };
    /**
     * Parses a date string using the default or provided format options
     * @param dateString The date string to parse
     * @param options The date format option overrides to be used (optional).
     */
    I18N.prototype.parseDate = function (dateString, options) {
        var formatString = get_date_format_string_1.getDateFormatString(this.getDateFormatOptions(options));
        return parse_date_1.parseDate(dateString, formatString);
    };
    /**
     * Formats a given `Number`, based on the provided `NumberFormatOptions`.
     *
     * @param value The `Number` value to be formatted.
     * @param options The number format option overrides to be used (optional).
     * @returns The formatted number string.
     */
    I18N.prototype.number = function (value, options) {
        return format_number_1.formatNumber(value, this.getNumberFormatOptions(options));
    };
    /**
     * Formats a given `Number` as a currency string, based on the provided `CurrencyFormatOptions`.
     *
     * @param value The `Number` value to be formatted.
     * @param options The currency format option overrides to be used (optional).
     * @returns The formatted currency string.
     */
    I18N.prototype.currency = function (value, options) {
        return format_currency_1.formatCurrency(value, this.getCurrencyFormatOptions(options));
    };
    /**
     * Formats a given [[MonetaryValue]] as a currency string, based on the provided [[MonetaryValueFormatOptions]].
     *
     * @param value The [[MonetaryValue]] value to be formatted.
     * @param options The monetary value format option overrides to be used (optional).
     * @returns The formatted monetary value string.
     */
    I18N.prototype.monetaryValue = function (_a, options) {
        var valueIncludingTax = _a.valueIncludingTax, valueExcludingTax = _a.valueExcludingTax;
        var mergedOptions = this.getMonetaryValueFormatOptions(options);
        var value = mergedOptions.taxMode === tax_mode_1.TaxMode.Inclusive ? valueIncludingTax : valueExcludingTax;
        return format_currency_1.formatCurrency(value, mergedOptions);
    };
    I18N.prototype.personName = function (personName, options) {
        return format_person_name_1.formatPersonName(personName, this.getTemplateFormatOptions({ personName: options }));
    };
    /**
     * Formats a given template string and parameters as a string.
     *
     * @param template The template string to be formatted.
     * @param parameters The parameters to be used.
     * @param options The template format option overrides to be used (optional).
     * @returns The formatted template string.
     */
    I18N.prototype.template = function (template, parameters, options) {
        return format_template_1.formatTemplate(template, parameters, this.getTemplateFormatOptions(options));
    };
    /**
     * Formats a given `Translatable` as a translated string.
     *
     * @param value The `Translatable` value to be formatted.
     * @param options The translatable format option overrides to be used (optional).
     * @returns The translated string.
     */
    I18N.prototype.translatable = function (value, options) {
        return format_translatable_1.formatTranslatable(value, this.getTranslatableLanguageTag(options));
    };
    /**
     * Converts a formatted number or currency `String` to its equivalent `Number` value.
     *
     * @param value The formatted string to be parsed.
     * @param options The parse number option overrides to be used (optional).
     * @returns The parsed `Number` value.
     */
    I18N.prototype.parse = function (value, options) {
        return parse_number_1.parseNumber(value, this.getDecimalSeparatorForParse(options));
    };
    return I18N;
}());
exports.I18N = I18N;
//# sourceMappingURL=i18n.js.map